import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ApiRequestService} from "../../../shared/api-request.service";
import {UtilsService} from "../../../shared/utils.service";
import {StorageService} from "../../../shared/storage.service";
import {ChartsUtilsService} from "../../charts-utils.service";
import {IncidentsFilter} from "../../../incidents/incidents-filter/incidents-filter.interface";

declare var google;

@Component({
  selector: 'app-incidents-by-type-combo-chart',
  templateUrl: './incidents-by-type-combo-chart.component.html',
  styleUrls: ['./incidents-by-type-combo-chart.component.scss']
})
export class IncidentsByTypeComboChartComponent implements OnInit, OnDestroy {

  // Config storage key. This must be unique when multiple containers are stored on one page.
  @Input('configStorageKey') configStorageKey: string;

  // The default incident type storage key.
  defaultIncidentTypeStorageKey: string;

  // The chart container.
  @ViewChild('chart_ref') chart_ref: ElementRef;

  // The chart object.
  chart: any;

  // Input to switch near realtime data polling on or off.
  @Input('enable_realtime_data') enable_realtime_data: boolean;
  // Input to change how long between intervals in seconds.
  @Input('realtime_data_interval_seconds') realtime_data_interval_seconds: number;
  // The realtime data interval for polling data.
  private realtime_data_interval: any;

  // The filters to use for API requests.
  @Input('filters') filters: IncidentsFilter;

  // Input to switch near realtime data polling on or off. This can only be single or multiple.
  @Input('query_type') query_type: string;

  // Used to pass a reference to this component back to the parent component.
  @Output() referenceEvent: EventEmitter<IncidentsByTypeComboChartComponent> = new EventEmitter<IncidentsByTypeComboChartComponent>();

  // Set the default incident type for this combo chart.
  @Input('default_incident_type') default_incident_type: string;

  // A list of available incident types to select from.
  incident_types: string[] = this.utils.getIncidentTypes();

  // The total number of incidents for this chart.
  total_incidents: number = 0;

  // Determine if the chart can be downloaded or not.
  @Input('canBeDownloaded') canBeDownloaded: boolean;

  // The name of the file that will be used when the chart is downloaded.
  @Input('chartDownloadFilename') chartDownloadFilename: string;

  // The name of the chart for the parent card title.
  cardTitle: string = 'Incidents by Type';

  chartData: any = [];

  constructor(
    private api: ApiRequestService,
    private utils: UtilsService,
    private storage: StorageService,
    public chartsUtils: ChartsUtilsService
  ) { }

  /**
   * Initializes the component by performing the following actions:
   * - Defines the default incident type storage key
   * - Validates the default config
   * - Loads the Google charts library
   * - Initializes the chart
   * - Gets the default incident type from storage
   * - Updates the download filename of the chart
   * - Gets the data from the API
   * - Sets up a realtime data interval (if enabled)
   * - Emits a reference event to the parent component
   *
   * @returns {void}
   */
  ngOnInit(): void {
    // Define the default incident type storage key.
    this.defaultIncidentTypeStorageKey = this.configStorageKey + '-default-incident-type';
    // Validate the default config.
    this.validateDefaultConfig();
    // Load the Google charts library into the software. It will only be loaded as needed.
    this.utils.loadExternalScript('google-charts', 'https://www.gstatic.com/charts/loader.js', () => {
      // Load the required libraries.
      google.charts.load('current', { packages: ['corechart'] });
      // Add a callback that will be executed when the chart libraries fully loaded.
      google.charts.setOnLoadCallback(async (): Promise<any> => {
        // Initialize the chart.
        this.chart = new google.visualization.ComboChart(this.chart_ref.nativeElement);
        // Get the default incident type from storage.
        const defaultIncidentTypeStorageObject = await this.storage.dbGet('charts', this.defaultIncidentTypeStorageKey);
        // Get the default incident type from storage. Set as Near Miss if it was not found.
        this.default_incident_type = defaultIncidentTypeStorageObject ? defaultIncidentTypeStorageObject.default_incident_type : 'Near Miss';
        // Set the card title.
        this.cardTitle = 'Incidents by Type (' + this.default_incident_type + ')';
        // Update the download filename of the chart.
        this.chartDownloadFilename = this.cardTitle + ' Combo Chart';
        // Get the data from the api.
        this.getData();
        // Check if realtime data should be pulled.
        if ( this.enable_realtime_data ) {
          // Create an interval to poll the API.
          this.realtime_data_interval = setInterval(() => {
            // Make a request to get the data.
            this.getData();
          }, this.realtime_data_interval_seconds * 1000);
        }
      });
    });
    // Send a reference to the parent component.
    this.referenceEvent.emit(this);
  }

  /**
   * Lifecycle hook called when the component is about to be destroyed.
   * It clears the near realtime data interval if it was set.
   */
  ngOnDestroy(): void {
    // Check if near realtime data polling is enabled and if the interval is set.
    if ( this.enable_realtime_data && this.realtime_data_interval ) {
      // Clear the interval to stop the data polling.
      clearInterval(this.realtime_data_interval);
    }
  }

  /**
   * Validates the configuration of a chart.
   * Ensures that certain properties are defined and sets default values if not provided.
   *
   * @return {void}
   */
  validateDefaultConfig(): void {
    // Ensure realtime data is defined and set to false if not provided.
    if ( typeof this.enable_realtime_data == 'undefined' ) {
      this.enable_realtime_data = false;
    }
    // Ensure realtime data interval seconds is defined and set to 60 if not provided.
    if ( typeof this.realtime_data_interval_seconds == 'undefined' ) {
      this.realtime_data_interval_seconds = 60;
    }
    // Ensure the query type is defined and set to 'single' if not provided. It can be 'multiple'.
    if ( typeof this.query_type == 'undefined' ) {
      this.query_type = 'single';
    }
    // Ensure the "default incident type" is defined and set to "Near Miss" if not provided.
    if ( typeof this.default_incident_type == 'undefined' ) {
      this.default_incident_type = 'Near Miss';
    }
    // Set the card title.
    this.cardTitle = 'Incidents by Type (' + this.default_incident_type + ')';
    // Ensure "can be downloaded" is defined and set to true if not provided.
    if ( typeof this.canBeDownloaded == 'undefined' ) {
      setTimeout(() => {
        this.canBeDownloaded = true;
      }, 1000); // Need delay due to change detection errors in dev.
    }
    // Ensure "chart download filename" is defined and set a default if not provided.
    if ( typeof this.chartDownloadFilename == 'undefined' ) {
      this.chartDownloadFilename = this.cardTitle + ' Combo Chart';
    }
  }

  /**
   * Updates the filters from the parent component.
   *
   * @param {any} filters - The new filters to update.
   * @return {void}
   */
  updateFiltersFromParentComponent(filters: any): void {
    // Update the filters.
    this.filters = filters;
  }

  /**
   * Retrieves data from the API and updates the chart visuals.
   *
   * @returns {void}
   */
  getData(): void {
    // Normalize the query params.
    const queryParams:{[p: string]: any} = this.utils.normalizeQueryParams({
      ...this.filters,
      query_type: this.query_type,
      date_range: this.filters.date_range.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(','),
      type: this.default_incident_type
    });
    // Get data from the API.
    this.api.makeRequest('get', 'v2/incidents/charts/by-type', {}, queryParams)
      .then((response) => {
        // Set the total number of incidents.
        this.total_incidents = response.total;
        // Store the chart data.
        this.chartData = response.data;
        // Draw the chart.
        this.drawChart();
      });
  }

  /**
   * Redraws the chart with updated data and options.
   *
   * @param {boolean} [includeTitle=false] - Specifies whether to include the chart title or not. Default is false.
   *
   * @returns {void} - This method does not return anything.
   */
  drawChart(includeTitle: boolean = false): void {
    // Draw the chart after fetching the data. This will redraw the chart for any subsequent calls.
    this.chart.draw(google.visualization.arrayToDataTable(this.chartData), {
      title: includeTitle ? this.cardTitle : '',
      titleTextStyle: {
        fontSize: 16,
        bold: true,
        color: 'darkgray'
      },
      tooltip: {
        ignoreBounds: true,
        isHtml: true
      },
      vAxis: {
        title: 'Incidents',
        format: '0'
      },
      hAxis: {
        title: 'Month'
      },
      seriesType: 'bars',
      series: {
        5: {
          type: 'line'
        }
      },
      colors: this.chartsUtils.defaultSystemChartColors()
    });
  }

  /**
   * Triggers when the incident type is changed.
   * Reloads the data from the API.
   *
   * @return {void}
   */
  async onDefaultIncidentTypeChanged(): Promise<any> {
    // Update the default incident type in storage.
    await this.storage.dbUpdateOrCreate('charts', { default_incident_type: this.default_incident_type , id: this.defaultIncidentTypeStorageKey});
    // Update the name of the chart.
    this.cardTitle = 'Incidents by Type (' + this.default_incident_type + ')';
    // Update the file download filename.
    this.chartDownloadFilename = this.cardTitle + ' Combo Chart';
    // Reload the data from the API.
    this.getData();
  }

  /**
   * Cleans up any data from storage that needs to be removed.
   *
   * @returns {Promise<void>} A Promise that resolves when the cleanup is completed.
   */
  async pruneStorage(): Promise<any> {
    // Clean any data from storage that need to be removed.
    await this.storage.dbDelete('charts', this.defaultIncidentTypeStorageKey);
  }
}
